import { Controller } from '@hotwired/stimulus';
import { Dropdown } from 'bootstrap';
import { fireChangeEvent } from '../../utils/accordion_change';
import { createOptionRenderer } from '../../utils/select/option_renderer';
import { searchOptions } from '../../utils/select/search_handler';
import { getAccordionLabel, getDisplayText } from '../../utils/select/display_handler';
import {
  addOptionToSelection,
  checkGroup,
  checkGroupItems,
  removeSelectedOptionById,
} from '../../utils/select/selection_handler';

const SELECTED_OPTION_CLASS = 'option--selected';
const FOCUSED_OPTION_CLASS = 'option--focused';
const KEYBOARD_NAVIGATING_CLASS = 'keyboard-navigating';

export default class SelectController extends Controller {
  static values = {
    placeholder: String,
    choices: { type: Array, default: [] },
    value: { type: Array, default: [] },
    multiple: { type: Boolean, default: false },
    deselectable: { type: Boolean, default: true },
    searchable: { type: Boolean, default: false },
    searchAtStart: { type: Boolean, default: false },
    maxSelectedChoices: { type: Number, default: null },
    grouping: { type: Boolean, default: false },
    withDefaultOption: { type: Boolean, default: false },
  };

  options = [];
  flatOptions = [];
  selectedOptions = [];
  currentValue = [];
  _initialized = false;
  isLoading = false;
  isDropdownOpen = false;
  _preventNextDropdownHide = false;
  _focusedOptionIndex = -1;

  static targets = [
    'input',
    'hiddenInput',
    'option',
    'optionsContainer',
    'toggleButton',
    'optionTemplate',
    'multiselectOptionTemplate',
    'optionGroupTemplate',
    'selectedOptionsTemplate',
    'loadingStateTemplate',
  ];

  connect() {
    this.currentValue = [...this.valueValue];

    this.optionRenderer = createOptionRenderer({
      templates: {
        option: this.optionTemplateTarget,
        multiselectOption: this.multiselectOptionTemplateTarget,
        optionGroup: this.optionGroupTemplateTarget,
        selectedOptions: this.selectedOptionsTemplateTarget,
        loading: this.loadingStateTemplateTarget,
      },
      multiple: this.multipleValue,
      maxSelectedChoices: this.maxSelectedChoicesValue,
      fieldId: this.element.dataset.id,
      grouping: this.groupingValue,
      withDefaultOption: this.withDefaultOptionValue,
      placeholder: this.placeholderValue,
    });

    this._updateOptions(this.choicesValue || []);
    fireChangeEvent(this.element, getAccordionLabel(this.selectedOptions));

    this.dropdown = Dropdown.getOrCreateInstance(this.inputTarget);

    this._initialized = true;
  }

  onFormReset(e) {
    if (!this._initialized || !e.target.contains(this.element)) {
      return;
    }

    this._applyValue([...this.valueValue]);
  }

  onUpdateChoices(e) {
    const newChoices = e.detail?.choices;
    if (newChoices === null) {
      this._showLoadingState();
      return;
    }

    this.selectedOptions = [];
    this._updateOptions(newChoices || []);
    this._filterValues(this.flatOptions);
    this.isLoading = false;
  }

  onDropdownShow() {
    this.isDropdownOpen = true;
    this.optionsContainerTarget.classList.remove(KEYBOARD_NAVIGATING_CLASS);

    if (this.searchableValue) {
      this._enterSearchMode();
    }

    if (!this.multipleValue) {
      this._scrollToSelectedOption();
    }

    this.inputTarget.focus();

    this._focusedOptionIndex = this._getNavigableOptions().findIndex((el) => el.checked);
  }

  onInputPointerDown() {
    this._preventNextDropdownHide = this.searchableValue && this.isDropdownOpen;
  }

  onToggleButtonClick(e) {
    e.stopPropagation();
    this.inputTarget.click();
  }

  onDropdownHide(e) {
    if (this._preventNextDropdownHide) {
      e.preventDefault();
      this._enterSearchMode();
    }

    this._preventNextDropdownHide = false;
  }

  onKeyDown(e) {
    if (!this.isDropdownOpen) {
      this._handleClosedKeyDown(e);
      return;
    }

    e.stopPropagation();

    const options = this._getNavigableOptions();

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        this._navigateTo(options, Math.min(this._focusedOptionIndex + 1, options.length - 1));
        break;
      }

      case 'ArrowUp': {
        e.preventDefault();
        this._navigateTo(options, Math.max(this._focusedOptionIndex - 1, 0));
        break;
      }

      case 'Enter': {
        e.preventDefault();
        if (this._focusedOptionIndex >= 0 && options[this._focusedOptionIndex]) {
          options[this._focusedOptionIndex].click();
        }
        break;
      }

      case 'Escape': {
        e.preventDefault();
        this.dropdown.hide();
        break;
      }

      case 'Tab': {
        this.dropdown.hide();
        break;
      }

      default:
        break;
    }
  }

  _handleClosedKeyDown(e) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      this.dropdown.show();
    }
  }

  _getNavigableOptions() {
    return this.optionTargets.filter((el) => !el.disabled && !el.closest('li.d-none'));
  }

  _navigateTo(options, nextOptionIndex) {
    this.optionsContainerTarget.classList.add(KEYBOARD_NAVIGATING_CLASS);
    this._highlightOption(options, this._focusedOptionIndex, nextOptionIndex);
    this._focusedOptionIndex = nextOptionIndex;
  }

  _highlightOption(options, prevIndex, nextIndex) {
    options[prevIndex]?.parentElement.classList.remove(FOCUSED_OPTION_CLASS);
    options[nextIndex]?.parentElement.classList.add(FOCUSED_OPTION_CLASS);
    options[nextIndex]?.parentElement.scrollIntoView({ block: 'nearest' });
  }

  onContainerMouseMove(e) {
    this.optionsContainerTarget.classList.remove(KEYBOARD_NAVIGATING_CLASS);
    this.optionTargets.forEach((el) => el.parentElement.classList.remove(FOCUSED_OPTION_CLASS));

    const input = this.optionTargets.find((el) => el.parentElement.contains(e.target));
    if (!input) {
      return;
    }

    const options = this._getNavigableOptions();
    const index = options.indexOf(input);
    if (index !== -1) {
      this._focusedOptionIndex = index;
    }
  }

  onDropdownHidden() {
    this.isDropdownOpen = false;
    this._refreshInputLabel();

    if (this.searchableValue) {
      this.inputTarget.blur();
    }
  }

  onDeleteAllSelectionsClick(e) {
    e.stopPropagation();
    this._resetValues();
    this._refreshInputLabel();
    this._refreshOptions(this.options);
  }

  onSearchInput(e) {
    this._focusedOptionIndex = -1;
    const query = e.target.value.toLowerCase();

    if (query === '') {
      this._refreshOptions(this.options);
      return;
    }

    const filteredOptions = searchOptions(this.options, query, this.searchAtStartValue);
    this._refreshOptions(filteredOptions, true);
    this.dropdown.show();
  }

  // non-multiple - option click
  onRadioOptionChange(e) {
    this.optionTargets.forEach((option) => {
      option.parentElement.classList.remove(SELECTED_OPTION_CLASS);
    });

    const { value } = e.target;

    if (value === '') {
      this._resetValues();
      this.dropdown.hide();
      return;
    }

    const isSelected = this.selectedOptions[0]?.id === value;
    if (isSelected) {
      if (!this.deselectableValue) {
        e.target.parentElement.classList.add(SELECTED_OPTION_CLASS);
        this.dropdown.hide();
        return;
      }
      this._resetValues();
      e.target.checked = false;
      if (this.withDefaultOptionValue) {
        const defaultInput = this.optionTargets.find((input) => input.value === '');
        if (defaultInput) {
          defaultInput.checked = true;
        }
      }
      return;
    }

    const selectedOption = this.options.find((option) => option.id === value);
    this.selectedOptions = [selectedOption];
    this._setValue([value]);

    e.target.parentElement.classList.add(SELECTED_OPTION_CLASS);

    this.dropdown.hide();
  }

  onOptionLabelClick(e) {
    e.stopPropagation();
  }

  // multiple - option click
  onOptionChange(e) {
    const { value, checked } = e.target;
    const selectedOption = this.flatOptions.find((option) => option.id === value);

    this.selectedOptions = checked
      ? addOptionToSelection(this.selectedOptions, selectedOption)
      : removeSelectedOptionById(this.selectedOptions, value);

    if (selectedOption.children) {
      // check all children
      this.selectedOptions = checkGroupItems(
        selectedOption.children,
        checked,
        this.selectedOptions,
        this._checkElement
      );
    }

    // check parent (group)
    if (selectedOption.group) {
      this.selectedOptions = checkGroup(
        selectedOption.group,
        this.flatOptions,
        this.selectedOptions,
        this._checkElement
      );
    }

    this._setValue(this.selectedOptions.map((option) => option.id));

    if (this.maxSelectedChoicesValue) {
      this.optionTargets.forEach((option) => {
        option.disabled = !option.checked && this.selectedOptions.length >= this.maxSelectedChoicesValue;
      });
    }

    if (this.searchableValue) {
      this._enterSearchMode();
      this.inputTarget.focus();
    }
  }

  choicesValueChanged() {
    if (!this._initialized) {
      return;
    }

    this.selectedOptions = [];
    this._updateOptions(this.choicesValue || []);
    this._filterValues(this.flatOptions);
  }

  valueValueChanged() {
    if (!this._initialized) {
      return;
    }

    this._applyValue([...this.valueValue]);
  }

  _setValue(ids) {
    this.currentValue = ids;
    this._notifyValueChange();
  }

  _applyValue(ids) {
    this.currentValue = ids;
    this.selectedOptions = this.flatOptions.filter((option) => ids.includes(option.id));
    this._checkGroups();
    this._refreshOptions(this.options);
    this._notifyValueChange();
  }

  _notifyValueChange() {
    this._refreshInputLabel();

    this.inputTarget.dispatchEvent(
      new CustomEvent('select:input-change', {
        detail: { value: this.selectedOptions },
        bubbles: true,
      })
    );

    if (this.hasHiddenInputTarget && this.hiddenInputTarget.value !== this.currentValue.join(',')) {
      this.hiddenInputTarget.value = this.currentValue.join(',');
      this.hiddenInputTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }

    fireChangeEvent(this.element, getAccordionLabel(this.selectedOptions));
  }

  // OPTIONS MANAGEMENT
  _updateOptions(newOptions) {
    const options = this._castIdsToString(newOptions);

    this.flatOptions = [];
    this.options = options;
    this._initOptions(options);
    this._checkGroups();
    this._refreshInputLabel();
    this._refreshOptions(options);
  }

  _initOptions(options, group) {
    options.forEach((option) => {
      this.flatOptions = [...this.flatOptions, { ...option, group }];
      if (this.currentValue.includes(option.id)) {
        this.selectedOptions = [...this.selectedOptions, { ...option, group }];
      }

      if (option.children) {
        this._initOptions(option.children, option.id);
      }
    });
  }

  _checkGroups() {
    const uniqueGroups = [...new Set(this.selectedOptions.map((option) => option.group))];
    uniqueGroups.forEach((group) => {
      this.selectedOptions = checkGroup(group, this.flatOptions, this.selectedOptions, this._checkElement);
    });
  }

  _refreshOptions(options, isSearching = false) {
    this._clearOptions();
    this.optionRenderer.addOptions({
      options,
      isSearching,
      target: this.optionsContainerTarget,
      selectedOptions: this.selectedOptions,
      isSearchable: this.searchableValue,
    });
  }

  _clearOptions() {
    this.optionsContainerTarget.innerHTML = '';
  }

  _refreshInputLabel() {
    this.inputTarget.value = getDisplayText(this.selectedOptions);
  }

  _enterSearchMode() {
    if (this.inputTarget.value === getDisplayText(this.selectedOptions)) {
      this.inputTarget.value = '';
    }

    if (!this.isLoading) {
      this._refreshOptions(this.options);
    }
  }

  _checkElement = (id, isChecked) => {
    const el = this.optionTargets.find((input) => input.value === id);
    if (el) {
      el.checked = isChecked;
    }
  };

  _scrollToSelectedOption() {
    if (!this.selectedOptions.length) {
      return;
    }

    requestAnimationFrame(() => {
      const selectedOptionElement = this.optionTargets.find((input) => input.value === this.selectedOptions[0].id);

      if (selectedOptionElement) {
        this.optionsContainerTarget.scrollTo({
          top: selectedOptionElement.parentElement.offsetTop,
          behavior: 'instant',
        });
      }
    });
  }

  _showLoadingState() {
    this._clearOptions();
    this.isLoading = true;
    this.optionRenderer.showLoading(this.optionsContainerTarget);
  }

  _castIdsToString(options) {
    const transformNode = (node) => ({
      ...node,
      id: node.equipmentType ? `${node.equipmentType}_${node.id}` : String(node.id),
      children: node.children?.map(transformNode),
    });

    return options.map(transformNode);
  }

  _resetValues() {
    this.selectedOptions = [];
    this._setValue([]);
  }

  _filterValues(choices) {
    this._setValue(this.currentValue.filter((value) => choices.some((choice) => choice.id === value)));
  }
}
